import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RARITY_DIMENSIONS, RARITY_DIMENSION_LABELS, SIGNAL_KINDS, SIGNAL_KIND_LABELS, STATEMENT_KINDS, type SignalKind, type StatementKind } from '@raro/domain';
import { Button, EmptyState, Field, Input, Label, Notice, Select, StatusDot, Tag, Textarea, cx, Thumb } from '@raro/ui';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { HYPOTHESIS_STATUS_LABELS, QUESTION_STATUS_LABELS, STATEMENT_KIND_LABELS, formatDate } from '../lib/format';
import type { EntitySummary, Hypothesis, Observation, Question, Relation, Signal } from '../lib/types';
import { EntityPicker } from './EntityPicker';
import { EntityChip } from './RelationList';

function useInvalidate(projectId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['summary', projectId] });
    void qc.invalidateQueries({ queryKey: ['entities', projectId] });
    void qc.invalidateQueries({ queryKey: ['overview'] });
  };
}

function InlineForm({ open, onOpen, label, children }: { open: boolean; onOpen: () => void; label: string; children: ReactNode }) {
  if (!open)
    return (
      <Button size="sm" variant="ghost" onClick={onOpen} style={{ alignSelf: 'flex-start' }}>
        {label}
      </Button>
    );
  return <div className="rr-card rr-stack rr-stack--4">{children}</div>;
}

// ───────────────────────── Perguntas ─────────────────────────

export function QuestionsSection({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const invalidate = useInvalidate(projectId);
  const list = useQuery({ queryKey: ['questions', projectId], queryFn: () => api.get<Question[]>(`/projects/${projectId}/questions`) });
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [context, setContext] = useState('');
  const [priority, setPriority] = useState(1);
  const create = useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/questions`, { text, context: context || undefined, priority }),
    onSuccess: () => {
      setText('');
      setContext('');
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['questions', projectId] });
      invalidate();
    },
  });
  const update = useMutation({
    mutationFn: ({ id, ...body }: { id: string; status?: Question['status']; answer?: string }) => api.patch(`/questions/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['questions', projectId] });
      invalidate();
    },
  });

  return (
    <section className="rr-section">
      <div className="rr-section__head">
        <Label>Perguntas</Label>
        <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>
          lista viva do que ainda não entendemos · {list.data?.filter((q) => q.status === 'open').length ?? 0} abertas
        </span>
      </div>
      {canEdit && (
        <InlineForm open={open} onOpen={() => setOpen(true)} label="+ nova pergunta">
          <Field label="Pergunta" htmlFor="q-text">
            <Input id="q-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="o que ainda não sabemos?" autoFocus />
          </Field>
          <Field label="Contexto" htmlFor="q-ctx" hint="de onde veio a dúvida? o que a tornou importante?">
            <Textarea id="q-ctx" rows={2} value={context} onChange={(e) => setContext(e.target.value)} />
          </Field>
          <Field label="Prioridade" htmlFor="q-pri">
            <Select id="q-pri" value={priority} onChange={(e) => setPriority(Number(e.target.value))}>
              <option value={0}>baixa</option>
              <option value={1}>normal</option>
              <option value={2}>alta</option>
              <option value={3}>crítica</option>
            </Select>
          </Field>
          {create.isError && <Notice tone="error">{(create.error as Error).message}</Notice>}
          <div className="rr-row">
            <Button variant="primary" onClick={() => create.mutate()} disabled={text.trim().length < 3 || create.isPending}>
              criar pergunta
            </Button>
            <Button variant="quiet" onClick={() => setOpen(false)}>
              cancelar
            </Button>
          </div>
        </InlineForm>
      )}
      {list.isSuccess && list.data.length === 0 && <EmptyState title="nenhuma pergunta aberta.">boas investigações começam pelo que não sabemos.</EmptyState>}
      <div>
        {list.data?.map((q) => (
          <QuestionRow key={q.id} q={q} canEdit={canEdit} onUpdate={(body) => update.mutate({ id: q.id, ...body })} />
        ))}
      </div>
    </section>
  );
}

function QuestionRow({ q, canEdit, onUpdate }: { q: Question; canEdit: boolean; onUpdate: (b: { status?: Question['status']; answer?: string }) => void }) {
  const [answering, setAnswering] = useState(false);
  const [answer, setAnswer] = useState(q.answer ?? '');
  return (
    <div className={cx('rr-question', q.status === 'answered' && 'rr-question--answered')}>
      <StatusDot tone={q.status === 'open' ? 'active' : q.status === 'investigating' ? 'hollow' : 'idle'} />
      <div>
        <div className="rr-question__text">{q.text}</div>
        {q.context && <div className="rr-question__ctx">{q.context}</div>}
        {q.answer && !answering && <div className="rr-question__answer">{q.answer}</div>}
        {answering && (
          <div className="rr-stack rr-stack--2" style={{ marginTop: 'var(--rr-space-2)' }}>
            <Textarea rows={3} value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="o que descobrimos?" autoFocus />
            <div className="rr-row">
              <Button size="sm" variant="primary" onClick={() => { onUpdate({ status: 'answered', answer }); setAnswering(false); }} disabled={answer.trim().length < 2}>
                registrar resposta
              </Button>
              <Button size="sm" variant="quiet" onClick={() => setAnswering(false)}>cancelar</Button>
            </div>
          </div>
        )}
      </div>
      <div className="rr-row" style={{ fontSize: 'var(--rr-text-xs)', color: 'var(--rr-text-3)' }}>
        {q.priority >= 2 && <Tag tone={q.priority === 3 ? 'accent' : 'outline'}>{q.priority === 3 ? 'crítica' : 'alta'}</Tag>}
        <span>{QUESTION_STATUS_LABELS[q.status]}</span>
        {canEdit && q.status !== 'answered' && !answering && (
          <>
            {q.status === 'open' && <Button size="sm" variant="quiet" onClick={() => onUpdate({ status: 'investigating' })}>investigar →</Button>}
            <Button size="sm" variant="quiet" onClick={() => setAnswering(true)}>responder</Button>
          </>
        )}
        {canEdit && q.status === 'answered' && <Button size="sm" variant="quiet" onClick={() => onUpdate({ status: 'open' })}>reabrir</Button>}
      </div>
    </div>
  );
}

// ───────────────────────── Sinais ─────────────────────────

export function SignalsSection({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const invalidate = useInvalidate(projectId);
  const list = useQuery({ queryKey: ['signals', projectId], queryFn: () => api.get<Signal[]>(`/projects/${projectId}/signals`) });
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [kind, setKind] = useState<SignalKind>('recurrence');
  const [direction, setDirection] = useState(0);
  const [evidence, setEvidence] = useState<EntitySummary[]>([]);
  const create = useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/signals`, { text, kind, direction, evidenceIds: evidence.map((e) => e.id) }),
    onSuccess: () => {
      setText('');
      setEvidence([]);
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['signals', projectId] });
      invalidate();
    },
  });
  return (
    <section className="rr-section">
      <div className="rr-section__head">
        <Label>Sinais</Label>
        <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>mudança, recorrência, anomalia ou fato que merece atenção</span>
      </div>
      {canEdit && (
        <InlineForm open={open} onOpen={() => setOpen(true)} label="+ novo sinal">
          <Field label="Sinal" htmlFor="s-text">
            <Input id="s-text" value={text} onChange={(e) => setText(e.target.value)} placeholder='ex.: "cor" aparece como critério recorrente de escolha' autoFocus />
          </Field>
          <div className="rr-row" style={{ gap: 'var(--rr-space-4)' }}>
            <Field label="Tipo" htmlFor="s-kind">
              <Select id="s-kind" value={kind} onChange={(e) => setKind(e.target.value as SignalKind)}>
                {SIGNAL_KINDS.map((k) => <option key={k} value={k}>{SIGNAL_KIND_LABELS[k]}</option>)}
              </Select>
            </Field>
            <Field label="Direção" htmlFor="s-dir">
              <Select id="s-dir" value={direction} onChange={(e) => setDirection(Number(e.target.value))}>
                <option value={1}>↑ sobe / cresce</option>
                <option value={0}>↗ neutro / recorrente</option>
                <option value={-1}>↓ cai / risco</option>
              </Select>
            </Field>
          </div>
          <Field label="Evidências de origem" htmlFor="s-ev" hint={evidence.length ? `${evidence.length} selecionada(s)` : 'opcional — de onde o sinal vem'}>
            <EntityPicker projectId={projectId} types={['evidence']} exclude={evidence} onSelect={(e) => setEvidence([...evidence, e])} />
          </Field>
          {create.isError && <Notice tone="error">{(create.error as Error).message}</Notice>}
          <div className="rr-row">
            <Button variant="primary" onClick={() => create.mutate()} disabled={text.trim().length < 3 || create.isPending}>criar sinal</Button>
            <Button variant="quiet" onClick={() => setOpen(false)}>cancelar</Button>
          </div>
        </InlineForm>
      )}
      {list.isSuccess && list.data.length === 0 && <EmptyState title="nenhum sinal ainda.">a partir do Sprint 3 a Machine também propõe sinais; hoje são marcados por pessoas.</EmptyState>}
      <div className="rr-stack rr-stack--3">
        {list.data?.map((s) => (
          <div key={s.id} className="rr-signal" style={{ alignItems: 'flex-start' }}>
            <span className={cx('rr-signal__icon', s.direction > 0 ? 'rr-signal__icon--up' : s.direction < 0 ? 'rr-signal__icon--down' : 'rr-signal__icon--flat')}>{s.direction > 0 ? '↑' : s.direction < 0 ? '↓' : '↗'}</span>
            <div className="rr-stack rr-stack--2" style={{ flex: 1 }}>
              <span style={{ color: 'var(--rr-text)' }}>{s.text}</span>
              <span className="rr-row rr-row--wrap" style={{ fontSize: 'var(--rr-text-xs)', color: 'var(--rr-text-3)' }}>
                <span>{SIGNAL_KIND_LABELS[s.kind]}</span>
                <span>{s.generatedBy === 'ai' ? 'proposto pela Machine' : 'marcado por pessoa'}</span>
                <span>{formatDate(s.createdAt)}</span>
                {s.relations.filter((r) => r.kind === 'derived_from').map((r) => (
                  <span key={r.id}>· de <EntityChip e={r.to} /></span>
                ))}
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ───────────────────────── Observações ─────────────────────────

export function ObservationsSection({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const invalidate = useInvalidate(projectId);
  const list = useQuery({ queryKey: ['observations', projectId], queryFn: () => api.get<Observation[]>(`/projects/${projectId}/observations`) });
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [kind, setKind] = useState<StatementKind>('observation');
  const [evidence, setEvidence] = useState<EntitySummary[]>([]);
  const create = useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/observations`, { text, kind, evidenceIds: evidence.map((e) => e.id) }),
    onSuccess: () => {
      setText('');
      setEvidence([]);
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['observations', projectId] });
      invalidate();
    },
  });
  return (
    <section className="rr-section">
      <div className="rr-section__head">
        <Label>Observações</Label>
        <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>interpretação sobre uma ou mais evidências · sempre diz o que é</span>
      </div>
      {canEdit && (
        <InlineForm open={open} onOpen={() => setOpen(true)} label="+ nova observação">
          <Field label="Observação" htmlFor="o-text">
            <Textarea id="o-text" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="o que isso sugere?" autoFocus />
          </Field>
          <Field label="Isso é…" htmlFor="o-kind" hint="evidência antes de afirmação: fato, observação, opinião ou inferência?">
            <Select id="o-kind" value={kind} onChange={(e) => setKind(e.target.value as StatementKind)}>
              {STATEMENT_KINDS.filter((k) => ['fact', 'observation', 'opinion', 'inference'].includes(k)).map((k) => <option key={k} value={k}>{STATEMENT_KIND_LABELS[k]}</option>)}
            </Select>
          </Field>
          <Field label="Evidências de origem" htmlFor="o-ev" hint={evidence.length ? `${evidence.length} selecionada(s)` : 'de onde a observação deriva'}>
            <EntityPicker projectId={projectId} types={['evidence']} exclude={evidence} onSelect={(e) => setEvidence([...evidence, e])} />
          </Field>
          {create.isError && <Notice tone="error">{(create.error as Error).message}</Notice>}
          <div className="rr-row">
            <Button variant="primary" onClick={() => create.mutate()} disabled={text.trim().length < 3 || create.isPending}>criar observação</Button>
            <Button variant="quiet" onClick={() => setOpen(false)}>cancelar</Button>
          </div>
        </InlineForm>
      )}
      {list.isSuccess && list.data.length === 0 && <EmptyState title="nenhuma observação." />}
      <div className="rr-stack rr-stack--3">
        {list.data?.map((o) => (
          <div key={o.id} className="rr-card rr-stack rr-stack--2">
            <p>{o.text}</p>
            <div className="rr-row rr-row--wrap" style={{ fontSize: 'var(--rr-text-xs)', color: 'var(--rr-text-3)' }}>
              <Tag tone="outline">{STATEMENT_KIND_LABELS[o.kind]}</Tag>
              <span>{o.generatedBy === 'ai' ? 'proposta pela Machine' : 'por pessoa'}</span>
              <span>{formatDate(o.createdAt)}</span>
              {o.relations.filter((r) => r.kind === 'derived_from').map((r) => <span key={r.id}>· de <EntityChip e={r.to} /></span>)}
              {o.relations.filter((r) => r.kind === 'contradicts' && r.fromId === o.id).map((r) => <span key={r.id} style={{ color: 'var(--rr-down)' }}>· contradiz <EntityChip e={r.to} /></span>)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ───────────────────────── Hipóteses ─────────────────────────

const TABLE_DIMS = RARITY_DIMENSIONS.filter((d) => d !== 'uncertainty' && d !== 'ownership');

export function HypothesesSection({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const invalidate = useInvalidate(projectId);
  const list = useQuery({ queryKey: ['hypotheses', projectId], queryFn: () => api.get<Hypothesis[]>(`/projects/${projectId}/hypotheses`) });
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [statement, setStatement] = useState('');
  const [rationale, setRationale] = useState('');
  const create = useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/hypotheses`, { title, statement, rationale: rationale || undefined }),
    onSuccess: () => {
      setTitle('');
      setStatement('');
      setRationale('');
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['hypotheses', projectId] });
      invalidate();
    },
  });
  return (
    <section className="rr-section">
      <div className="rr-section__head">
        <Label>Hipóteses</Label>
        <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>algo que acreditamos que possa explicar ou transformar · nunca é fato</span>
      </div>
      {canEdit && (
        <InlineForm open={open} onOpen={() => setOpen(true)} label="+ nova hipótese">
          <Field label="Título" htmlFor="h-title">
            <Input id="h-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="ex.: Festa pela cor" autoFocus />
          </Field>
          <Field label="Afirmação" htmlFor="h-stmt" hint="o que acreditamos? escreva como algo que pode estar errado.">
            <Textarea id="h-stmt" rows={3} value={statement} onChange={(e) => setStatement(e.target.value)} />
          </Field>
          <Field label="Por quê" htmlFor="h-rat" hint="o que nos levou a pensar isso?">
            <Textarea id="h-rat" rows={2} value={rationale} onChange={(e) => setRationale(e.target.value)} />
          </Field>
          {create.isError && <Notice tone="error">{(create.error as Error).message}</Notice>}
          <div className="rr-row">
            <Button variant="primary" onClick={() => create.mutate()} disabled={title.trim().length < 2 || statement.trim().length < 3 || create.isPending}>criar hipótese</Button>
            <Button variant="quiet" onClick={() => setOpen(false)}>cancelar</Button>
          </div>
        </InlineForm>
      )}
      {list.isSuccess && list.data.length === 0 && <EmptyState title="nenhuma hipótese.">a Machine apresenta evidências. a Raro julga.</EmptyState>}
      {list.data && list.data.length > 0 && (
        <div className="rr-stack rr-stack--2">
          <div className="rr-hyp-head">
            <span />
            <span />
            {TABLE_DIMS.map((d) => <span key={d}>{RARITY_DIMENSION_LABELS[d]}</span>)}
            <span />
          </div>
          {list.data.map((h) => (
            <Link key={h.id} to={`/hipoteses/${h.id}`} className="rr-hyp-row">
              <span className="rr-hyp-row__num">{String(h.number).padStart(2, '0')}</span>
              <div className="rr-row" style={{ gap: 'var(--rr-space-3)', minWidth: 0 }}>
                <Thumb src={h.image?.thumbUrl ?? h.image?.url} size={48} ratio="4 / 3" />
                <div style={{ minWidth: 0 }}>
                  <div className="rr-hyp-row__title">{h.title}</div>
                  <div className="rr-hyp-row__sub">{h.statement.length > 90 ? `${h.statement.slice(0, 87)}…` : h.statement} · {HYPOTHESIS_STATUS_LABELS[h.status]}</div>
                </div>
              </div>
              {TABLE_DIMS.map((d) => (
                <div key={d} className={cx('rr-mini-meter', h.scores[d] === undefined && 'rr-mini-meter--empty')} style={{ ['--v' as string]: `${h.scores[d] ?? 0}%` }} title={`${RARITY_DIMENSION_LABELS[d]}: ${h.scores[d] ?? '—'}`} />
              ))}
              <div className="rr-hyp-row__counts">
                <b>{h.supports ?? 0}</b> ↑ · <b>{h.contradicts ?? 0}</b> ↓
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

// ───────────────────────── Contradições ─────────────────────────

export function ContradictionsSection({ projectId }: { projectId: string }) {
  const list = useQuery({ queryKey: ['contradictions', projectId], queryFn: () => api.get<Relation[]>(`/projects/${projectId}/contradictions`) });
  return (
    <section className="rr-section">
      <div className="rr-section__head">
        <Label>Contradições</Label>
        <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>diferenças entre discurso e comportamento, evidência contra hipótese</span>
      </div>
      {list.isSuccess && list.data.length === 0 && <EmptyState title="nenhuma contradição registrada.">relacione uma evidência ou observação a uma hipótese com “contradiz”. no Sprint 3, REMAR CONTRA procura por você.</EmptyState>}
      <div className="rr-stack rr-stack--3">
        {list.data?.map((r) => (
          <div key={r.id} className="rr-contra">
            <div className="rr-contra__side"><EntityChip e={r.from} /></div>
            <div className="rr-contra__x">×</div>
            <div className="rr-contra__side">
              <EntityChip e={r.to} />
              {r.note && <div className="rr-rel__note">{r.note}</div>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
